import { useState } from "react";
import { Download } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { cn } from "@/lib/utils";
import { initials } from "@/lib/formatters";
import { resolveAvatarColor } from "@/lib/avatarColor";
import type { AnalyticsPeriod } from "@/api/analytics";
import type { ProjectMember } from "@/api/types";
import { usePermissions } from "@/hooks/usePermissions";

interface AnalyticsHeaderProps {
  period: AnalyticsPeriod;
  onPeriodChange: (period: AnalyticsPeriod) => void;
  customRange: { start: string; end: string } | null;
  onCustomRangeChange: (range: { start: string; end: string } | null) => void;
  ownerId: string;
  onOwnerChange: (id: string) => void;
  members: ProjectMember[];
}

const PERIOD_OPTIONS: AnalyticsPeriod[] = ["7d", "30d", "90d", "12m"];
const PERIOD_LABEL: Record<AnalyticsPeriod, string> = {
  "7d": "7d",
  "30d": "30d",
  "90d": "90d",
  "12m": "12m",
  custom: "Custom",
};

export function AnalyticsHeader({
  period,
  onPeriodChange,
  customRange,
  onCustomRangeChange,
  ownerId,
  onOwnerChange,
  members,
}: AnalyticsHeaderProps) {
  const canExport = usePermissions().canExportAnalytics;
  const [customOpen, setCustomOpen] = useState(false);
  const [draftStart, setDraftStart] = useState(customRange?.start ?? "");
  const [draftEnd, setDraftEnd] = useState(customRange?.end ?? "");

  function applyCustom() {
    if (!draftStart || !draftEnd) return;
    onCustomRangeChange({ start: draftStart, end: draftEnd });
    onPeriodChange("custom");
    setCustomOpen(false);
  }

  return (
    <div className="flex flex-wrap items-center justify-between gap-3">
      {/* Period toggle group + custom popover */}
      <div className="inline-flex h-9 rounded-lg border border-slate-200 bg-slate-50 p-0.5">
        {PERIOD_OPTIONS.map((p) => (
          <button
            key={p}
            type="button"
            onClick={() => onPeriodChange(p)}
            aria-pressed={period === p}
            className={cn(
              "inline-flex h-8 items-center rounded-md px-3 text-xs font-medium transition-colors",
              period === p
                ? "bg-white text-slate-900 shadow-sm-tile"
                : "text-slate-500 hover:text-slate-700",
            )}
          >
            {PERIOD_LABEL[p]}
          </button>
        ))}
        <Popover open={customOpen} onOpenChange={setCustomOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              aria-pressed={period === "custom"}
              className={cn(
                "inline-flex h-8 items-center rounded-md px-3 text-xs font-medium transition-colors",
                period === "custom"
                  ? "bg-white text-slate-900 shadow-sm-tile"
                  : "text-slate-500 hover:text-slate-700",
              )}
            >
              Custom
            </button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-72 p-3">
            <div className="space-y-2">
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                Start
              </label>
              <Input
                type="date"
                value={draftStart}
                onChange={(e) => setDraftStart(e.target.value)}
                className="h-9 rounded-lg border-slate-200"
              />
              <label className="text-[11px] font-medium uppercase tracking-wider text-slate-500">
                End
              </label>
              <Input
                type="date"
                value={draftEnd}
                onChange={(e) => setDraftEnd(e.target.value)}
                className="h-9 rounded-lg border-slate-200"
              />
              <div className="flex items-center justify-end gap-2 pt-1">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setCustomOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  variant="primary"
                  size="sm"
                  disabled={!draftStart || !draftEnd}
                  onClick={applyCustom}
                >
                  Apply
                </Button>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      </div>

      <div className="flex items-center gap-2">
        {/* Owner filter */}
        <div className="flex items-center gap-2">
          <span className="text-xs text-slate-500">Owner</span>
          <Select value={ownerId} onValueChange={onOwnerChange}>
            <SelectTrigger className="h-9 w-[200px] rounded-lg border-slate-200 bg-white">
              <SelectValue placeholder="All members" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All members</SelectItem>
              {members.map((m) => {
                const avatar = resolveAvatarColor(undefined, m.id);
                return (
                  <SelectItem key={m.id} value={m.id}>
                    <span className="flex items-center gap-2">
                      <Avatar className="h-5 w-5 border border-slate-200">
                        <AvatarFallback
                          className={cn(
                            "text-[9px] font-semibold",
                            avatar.bg,
                            avatar.text,
                          )}
                        >
                          {initials(m.full_name)}
                        </AvatarFallback>
                      </Avatar>
                      <span className="truncate">{m.full_name}</span>
                    </span>
                  </SelectItem>
                );
              })}
            </SelectContent>
          </Select>
        </div>
        {/* Export PDF — gated on analytics.export. The action
         *  itself is V2 (still disabled with a "Coming soon"
         *  tooltip), but the button is hidden entirely from
         *  operators without the export permission so it doesn't
         *  read as a teaser they can't actually use. */}
        {canExport && (
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <span tabIndex={0}>
                  <Button
                    variant="outline"
                    size="sm"
                    disabled
                    className="rounded-full"
                  >
                    <Download className="mr-2 h-3.5 w-3.5" />
                    Export PDF
                  </Button>
                </span>
              </TooltipTrigger>
              <TooltipContent side="bottom">Coming soon</TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </div>
  );
}
